import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, MapPin, Users, Camera, Mic, FileText, Heart, Shield } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';

const FieldworkPage = () => {
  const fieldActivities = [
    {
      icon: <Users className="w-6 h-6" />,
      title: "Participant Observation",
      description: "Embedded engagement in everyday social, ritual, and political life of communities"
    },
    {
      icon: <Mic className="w-6 h-6" />,
      title: "Oral History & Testimony",
      description: "Life histories, kinship narratives, ancestral memory, and intergenerational storytelling"
    },
    {
      icon: <Camera className="w-6 h-6" />,
      title: "Audio-Visual Ethnography",
      description: "Filmmaking, photography, and soundscape recordings to preserve intangible cultural heritage"
    },
    {
      icon: <Users className="w-6 h-6" />,
      title: "Community Workshops",
      description: "Focus groups and participatory projects for climate and health research"
    },
    {
      icon: <MapPin className="w-6 h-6" />,
      title: "Ecological Surveys",
      description: "Biodiversity mapping with citizen scientists and school twinning programs"
    },
    {
      icon: <FileText className="w-6 h-6" />,
      title: "Archival Research",
      description: "Museum and archive-based fieldwork with partner institutions worldwide"
    }
  ];

  const ethicsCommitments = [
    "Prior, informed, and ongoing community consent",
    "Co-authorship and shared ownership of data and outcomes",
    "Compensation for time, hospitality, and traditional knowledge shared",
    "Respect for spiritual, ritual, and taboo boundaries"
  ];

  const regions = [
    "Nagaland, Manipur, Assam, Arunachal Pradesh, and Meghalaya",
    "The India–Myanmar borderlands",
    "Highland zones in Bhutan, Nepal, and Northern Myanmar",
    "Comparative engagements in the Arctic, Latin America, and Southeast Asia"
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-green-900 to-green-700 text-white py-24">
        <div className="absolute inset-0 bg-black/40"></div>
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://customer-assets.emergentagent.com/job_institute-clone/artifacts/cj32pbr1_NEN-Biodiversity%20Festival.png)'
          }}
        ></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              Fieldwork and Data Collection
            </h1>
            <p className="text-2xl text-green-100 leading-relaxed">
              Deep Engagement, Collaborative Methods, and Ethical Practice
            </p>
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="prose prose-lg max-w-none">
              <p className="text-gray-700 leading-relaxed mb-6 text-lg">
                At the heart of The Highland Institute's research lies a commitment to <strong>immersive, long-term 
                fieldwork</strong> and the <strong>co-production of knowledge</strong> with highland communities across 
                Northeast India and beyond. Our approach is grounded in ethnographic depth, interdisciplinary 
                collaboration, and mutual trust with communities who live in some of the most culturally rich yet 
                structurally marginal regions of Asia.
              </p>
              <p className="text-gray-700 leading-relaxed text-lg">
                We believe fieldwork is not only about gathering data but about building meaningful relationships—across 
                generations, epistemologies, and lifeworlds.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Where We Work */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-gray-900 mb-8 text-center">Where We Work</h2>
            <p className="text-gray-700 text-center mb-8">
              Our researchers conduct fieldwork in a wide range of locations:
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {regions.map((region, index) => (
                <div key={index} className="flex items-start bg-white p-6 rounded-lg shadow-sm">
                  <MapPin className="w-5 h-5 text-green-600 mt-1 mr-3 flex-shrink-0" />
                  <span className="text-gray-700">{region}</span>
                </div>
              ))}
            </div>
            <p className="text-gray-600 text-center mt-8">
              Many of our researchers live and work in the communities they study, enabling a depth of local 
              engagement rarely found in short-term academic research.
            </p>
          </div>
        </div>
      </section>

      {/* What We Do in the Field */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-4xl font-bold text-gray-900 mb-8 text-center">What We Do in the Field</h2>
            <p className="text-gray-700 text-center mb-12">
              Fieldwork at the Institute is methodologically diverse and context-sensitive, with techniques drawn 
              from anthropology, history, environmental science, religious studies, health studies, and visual ethnography.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {fieldActivities.map((activity, index) => (
                <Card key={index} className="hover:shadow-xl transition-shadow">
                  <CardHeader>
                    <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-4 text-green-600">
                      {activity.icon}
                    </div>
                    <CardTitle className="text-lg">{activity.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-600 text-sm">{activity.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Ethics and Community Involvement */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center justify-center mb-8">
              <Shield className="w-12 h-12 text-green-600 mr-4" />
              <h2 className="text-4xl font-bold text-gray-900">Ethics and Community Involvement</h2>
            </div>
            <div className="bg-white p-8 rounded-lg shadow-md">
              <p className="text-gray-700 leading-relaxed mb-6">
                We adhere to a strong code of research ethics, shaped by community consent, transparency, and 
                cultural protocols. Key ethical commitments include:
              </p>
              <div className="space-y-4">
                {ethicsCommitments.map((commitment, index) => (
                  <div key={index} className="flex items-start">
                    <Heart className="w-5 h-5 text-green-600 mt-1 mr-3 flex-shrink-0" />
                    <span className="text-gray-700">{commitment}</span>
                  </div>
                ))}
              </div>
              <div className="border-l-4 border-green-600 pl-6 mt-6">
                <p className="text-gray-700 leading-relaxed">
                  We actively involve local researchers, elders, school groups, and grassroots organisations in our 
                  projects—not simply as informants, but as <strong>co-producers of knowledge</strong>.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Student Fieldwork and PGCertR */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-gray-900 mb-8">Student Fieldwork and PGCertR Integration</h2>
            <div className="prose prose-lg max-w-none">
              <p className="text-gray-700 leading-relaxed mb-6">
                Students enrolled in the Postgraduate Certificate in Research (PGCertR) participate in mentored 
                fieldwork as part of their training. Each student is paired with a member of the Highland Fellowship 
                and supported throughout their time in the field, from research design and risk assessment to community 
                entry and data analysis.
              </p>
              <p className="text-gray-700 leading-relaxed">
                Fieldwork typically takes place between September and November, followed by a Winter Research Colloquium, 
                where students present their preliminary findings to an academic and community audience.
              </p>
            </div>
            <div className="mt-8 text-center">
              <Link to="/study-with-us">
                <Button className="bg-green-600 hover:bg-green-700 px-8 py-6 text-lg">
                  Learn About Study Programmes
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Data Curation */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-gray-900 mb-8 text-center">Data Curation and Accessibility</h2>
            <div className="bg-white p-8 rounded-lg shadow-md">
              <p className="text-gray-700 leading-relaxed mb-6">
                We are developing a <strong>Field Archive and Oral Literature Repository</strong>, which will house 
                a growing collection of:
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div className="flex items-start">
                  <ArrowRight className="w-4 h-4 text-green-600 mt-1 mr-2 flex-shrink-0" />
                  <span className="text-gray-700">Field recordings</span>
                </div>
                <div className="flex items-start">
                  <ArrowRight className="w-4 h-4 text-green-600 mt-1 mr-2 flex-shrink-0" />
                  <span className="text-gray-700">Oral literature transcriptions</span>
                </div>
                <div className="flex items-start">
                  <ArrowRight className="w-4 h-4 text-green-600 mt-1 mr-2 flex-shrink-0" />
                  <span className="text-gray-700">Ecological maps</span>
                </div>
                <div className="flex items-start">
                  <ArrowRight className="w-4 h-4 text-green-600 mt-1 mr-2 flex-shrink-0" />
                  <span className="text-gray-700">Photographic and audiovisual materials</span>
                </div>
                <div className="flex items-start">
                  <ArrowRight className="w-4 h-4 text-green-600 mt-1 mr-2 flex-shrink-0" />
                  <span className="text-gray-700">Annotated field diaries</span>
                </div>
              </div>
              <p className="text-gray-700 leading-relaxed">
                While some of this data is restricted due to cultural or ethical considerations, we are committed to 
                making large portions open-access through Highlander Press, our video library, and community-facing 
                knowledge platforms.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-green-600 to-green-700 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-6">Get Involved</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            If you are a researcher, student, or institution interested in participating in or supporting field-based 
            research with The Highland Institute, please contact us.
          </p>
          <p className="text-lg mb-8">
            Together, we believe that <em>place-based research</em> can illuminate not only the complexities of 
            Highland Asia but also broader questions of knowledge, resilience, and relational life.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link to="/contact">
              <Button className="bg-white text-green-600 hover:bg-gray-100 px-8 py-6 text-lg">
                Contact Us
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <Link to="/research">
              <Button variant="outline" className="border-white text-white hover:bg-white/10 px-8 py-6 text-lg">
                View Research Projects
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FieldworkPage;
